'use client';

import { useEffect, useState } from 'react';
import { asText, Content, isFilled, RichTextField } from '@prismicio/client';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

import {
  Box,
  Button,
  Typography,
  HelpMessage,
  ValidationMessage,
} from '@preventx/oxygen';
import { RichTextDefault } from '@preventx/prismic-services';

// Types
import { ProductVariantOption } from '@/types/products/productVariants';

// Services
import { applicationRerouteUrl } from '@/services/routing/application';

// Contexts
import { useProductDetailsContext } from '@/context/ProductDetailsProvider';

type WithProductVariantsProps = {
  options: ProductVariantOption[];
  slice: Content.ProductsHeroHeadlineSliceWithProductVariants;
};

export const WithProductVariants = ({
  slice,
  options,
}: WithProductVariantsProps) => {
  const { primary } = slice;
  const {
    select_product_label,
    product_variant_label,
    product_variant_not_selected,
    product_selection_help_message,
  } = primary;

  const { selectedProduct, setSelectedProduct } = useProductDetailsContext();

  const [errorMessage, setErorMessage] = useState<RichTextField>([]);

  useEffect(() => {
    if (selectedProduct) {
      setErorMessage([]);
    }
  }, [selectedProduct]);

  const onClick = () => {
    setErorMessage([]);

    if (!selectedProduct) {
      setErorMessage(product_variant_not_selected);

      return;
    }

    location.href =
      applicationRerouteUrl({
        application: selectedProduct.application,
        params: { productKey: selectedProduct.productKey },
      }) ?? '';
  };

  return (
    <>
      <Box component="fieldset" addFlow className="product-select">
        <Typography component="legend" className="question-title">
          {asText(product_variant_label)}
        </Typography>

        {isFilled.richText(product_selection_help_message) && (
          <HelpMessage>{asText(product_selection_help_message)}</HelpMessage>
        )}

        {errorMessage && (
          <ValidationMessage>
            <RichTextDefault content={errorMessage} />
          </ValidationMessage>
        )}

        {options.map(({ id, price, label, productKey, application }) => (
          <Box
            key={id}
            addCluster
            component="div"
            data-type="bordered"
            className="fieldset-item"
          >
            <input
              id={id}
              type="radio"
              name="kit-type"
              autoComplete="off"
              onChange={() =>
                setSelectedProduct({
                  price: price,
                  productKey: productKey,
                  application: application,
                })
              }
            />

            <label htmlFor={id} className="has-details repel">
              <Typography component="span">{label}</Typography>
              <Typography component="strong">{price}</Typography>
            </label>
          </Box>
        ))}
      </Box>

      <Box component="div" addCluster>
        <Button
          size="lg"
          type="button"
          variant="primary"
          onClick={onClick}
          icon={{ definition: faArrowRight, position: 'right' }}
        >
          {asText(select_product_label)}
        </Button>
      </Box>
    </>
  );
};
